import React, { Component, ReactNode } from 'react';
import clsx from 'clsx';
import { Button, Tooltip } from 'elmo-elements';
import {
  PlaylistAddIcon,
  WarningOutlinedIcon,
} from 'element';
import './RosterUserLegend.scss';
import { connect } from 'react-redux';
import { StoreState } from 'state/types';
import { BOX_ROSTER_BULK_TOGGLE_SELECTION_BY_USER } from 'state/Roster/BulkActions/actions';
import { FeatureFlag } from 'element/feature-flags-components';
import { CheckCircleIcon } from 'elmo-elements/icons';
import { getDutyIcon } from 'lib/helpers/dutyIcons';

export type Duration = {
  icon: ReactNode;
  quantity: number;
  time: string;
  isVisible: boolean;
  contractedTime?: string;
};

export type UserDuty = {
  id: string;
  key: string;
  name: string;
};

type StateProps = {
  bulkDeleteIsActive: boolean;
};

type DispatchProps = {
  toggleSelectionByUser: (userId: string | null) => void;
};

type Props = {
  type?: 'single-row' | 'multi-row' | 'always-single-row';
  icon: ReactNode;
  label: string;
  durations: Duration[];
  onClick?: () => void;
  isDisabled?: boolean;
  bulkSelect?: boolean;
  userId?: string | null;
  pageType: 'week' | 'day';
  duties?: UserDuty[];
} & StateProps &
  DispatchProps;

type State = {
  isOpened: boolean;
};

export class RosterUserLegendComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isOpened: false,
    };
  }

  toggleFullInfo = () => {
    this.setState({
      isOpened: !this.state.isOpened,
    });
  };

  onClickHandler = () => {
    const { bulkSelect, bulkDeleteIsActive, userId } = this.props;
    if (bulkSelect && bulkDeleteIsActive && typeof userId !== 'undefined') {
      this.props.toggleSelectionByUser(userId);
    }
  };

  /**
   * Converts time string (e.g., "33:00") to minutes for comparison
   */
  timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  /**
   * Determines the colour class based on scheduled vs contracted hours
   * Yellow: scheduled < contracted
   * Green: scheduled = contracted
   * Red: scheduled > contracted
   */
  getTimeComparisonColour = (
    scheduledTime: string,
    contractedTime?: string
  ): string => {
    if (!contractedTime) {
      return '';
    }

    const contractedMinutes = this.timeToMinutes(contractedTime);
    if (contractedMinutes === 0) {
      return '';
    }

    const scheduledMinutes = this.timeToMinutes(scheduledTime);

    if (scheduledMinutes < contractedMinutes) {
      return 'elmo-roster-user-legend__time--under';
    } else if (scheduledMinutes === contractedMinutes) {
      return 'elmo-roster-user-legend__time--equal';
    } else {
      return 'elmo-roster-user-legend__time--over';
    }
  };

  /**
   * Returns the appropriate icon based on time comparison
   */
  getTimeComparisonIcon = (
    scheduledTime: string,
    contractedTime?: string
  ): ReactNode => {
    if (!contractedTime) {
      return null;
    }

    const contractedMinutes = this.timeToMinutes(contractedTime);
    if (contractedMinutes === 0) {
      return null;
    }

    const scheduledMinutes = this.timeToMinutes(scheduledTime);

    if (scheduledMinutes === contractedMinutes) {
      return <CheckCircleIcon />;
    } else {
      return <WarningOutlinedIcon />;
    }
  };

  render() {
    const {
      durations,
      type = 'single-row',
      icon,
      onClick,
      label,
      isDisabled,
      duties,
    } = this.props;
    const { isOpened } = this.state;

    const visibleDurations = durations.filter((duration) => duration.isVisible);

    return (
      <div
        onClick={this.onClickHandler}
        className={clsx([
          'elmo-roster-user-legend',
          'ui-roster-legend',
          `elmo-roster-user-legend--${type}`,
          isDisabled && 'is-disabled',
          !visibleDurations.length && 'elmo-roster-user-legend--no-label',
        ])}
      >
        <div
          className="elmo-roster-user-legend__mobile-card-placeholder"
          onClick={this.toggleFullInfo}
        >
          <div className="elmo-roster-user-legend__icon-wrapper">{icon}</div>
        </div>

        <div
          className={clsx([
            'elmo-roster-user-legend__desktop-card-placeholder',
            isOpened && 'is-opened',
            isDisabled && 'is-disabled',
          ])}
          onClick={this.toggleFullInfo}
        >
          <div className="elmo-roster-user-legend__icon-wrapper">{icon}</div>

          <div className="elmo-roster-user-legend__content-wrapper">
            <div className="elmo-roster-user-legend__label" title={label}>
              {label}
            </div>
            {durations.map(
              (
                { time, quantity, icon: durationIcon, isVisible, contractedTime },
                key
              ) => (
                <div
                  className="elmo-roster-user-legend__duration"
                  key={key}
                  style={{
                    display: isVisible ? 'flex' : 'none',
                  }}
                >
                  <div>{durationIcon}</div>

                  <div className="elmo-roster-user-legend__duration-quantity">
                    {quantity}
                  </div>

                  <div className="elmo-roster-user-legend__duration-time">
                    <FeatureFlag name="contractedHours">
                      <FeatureFlag.On>
                        {contractedTime ? (
                          <div className="elmo-roster-user-legend__time-comparison">
                            <span
                              className={clsx(
                                'elmo-roster-user-legend__time-scheduled',
                                this.getTimeComparisonColour(time, contractedTime)
                              )}
                            >
                              {time}
                            </span>
                            <span className="elmo-roster-user-legend__time-separator">
                              {' / '}
                            </span>
                            <span className="elmo-roster-user-legend__time-contracted">
                              {contractedTime}
                            </span>
                          </div>
                        ) : (
                          time
                        )}
                      </FeatureFlag.On>
                      <FeatureFlag.Off>{time}</FeatureFlag.Off>
                    </FeatureFlag>
                  </div>

                  <FeatureFlag name="contractedHours">
                    <FeatureFlag.On>
                      {contractedTime && (
                        <div
                          className={clsx(
                            'elmo-roster-user-legend__time-icon',
                            this.getTimeComparisonColour(time, contractedTime)
                          )}
                        >
                          {this.getTimeComparisonIcon(time, contractedTime)}
                        </div>
                      )}
                    </FeatureFlag.On>
                  </FeatureFlag>
                </div>
              )
            )}

            <FeatureFlag name="duties">
              <FeatureFlag.On>
                {duties && duties.length > 0 && (
                  <div className="elmo-roster-user-legend__duties">
                    {duties.map((duty) => {
                      const icon = getDutyIcon(duty.key, 16);
                      return icon ? (
                        <Tooltip key={duty.id} title={duty.name} placement="top">
                          <span className="elmo-roster-user-legend__duty-icon">
                            {icon}
                          </span>
                        </Tooltip>
                      ) : null;
                    })}
                  </div>
                )}
              </FeatureFlag.On>
            </FeatureFlag>
          </div>

          {this.props.pageType === 'day' && !!onClick && (
            <Button
              className="add-empty-roster-row-button"
              icon={<PlaylistAddIcon />}
              isText={true}
              onClick={onClick}
              ariaLabel="Add row"
              tooltip={{
                title: 'Add row',
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: StoreState): StateProps => ({
  bulkDeleteIsActive: state.rosterBulkActions.bulkDelete.isOpened,
});

const mapToDispatchProps: DispatchProps = {
  toggleSelectionByUser: BOX_ROSTER_BULK_TOGGLE_SELECTION_BY_USER,
};

export const RosterUserLegend = connect(
  mapStateToProps,
  mapToDispatchProps
)(RosterUserLegendComponent);
